import { useState, useMemo, useRef, useEffect } from 'react';
import type { Service } from '@booking/shared';
import { useAvailability, isWithinRange, addMonths } from '../hooks/useAvailability';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  buildMonthGrid,
  formatMonthYear,
  formatShortDate,
  formatHora,
  startOfToday,
  toDateString,
} from '../lib/dates';
import { Spinner } from './Spinner';

type Props = {
  doctorId: string;
  service: Service;
  /* lo que realmente dura la cita, cuando el servicio se arma por zonas */
  minutos?: number;
  onPick: (date: string, time: string) => void;
  onBack: () => void;
};

/**
 * Combined month calendar + day slot panel. Clicking a day reveals that day's
 * slots in a side panel (no separate step). Clicking a slot advances.
 */
export function DateTimePicker({ doctorId, service, minutos, onPick, onBack }: Props) {
  const today = useMemo(() => new Date(), []);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const [view, setView] = useState({
    year: today.getFullYear(),
    month: today.getMonth() + 1,
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickListRef = useRef<HTMLUListElement>(null);
  const activeOptionRef = useRef<HTMLButtonElement>(null);

  // When the month dropdown opens, scroll the current month into the middle
  // instead of starting at the top of the list.
  useEffect(() => {
    if (!pickerOpen) return;
    const list = pickListRef.current;
    const active = activeOptionRef.current;
    if (list && active) {
      list.scrollTop = active.offsetTop - list.clientHeight / 2 + active.clientHeight / 2;
    }
  }, [pickerOpen]);

  const { data, isLoading } = useAvailability(doctorId, service.id, view.year, view.month, minutos);

  const slotsByDay = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const d of data?.days ?? []) {
      if (d.slots.length > 0) map.set(d.date, d.slots);
    }
    return map;
  }, [data]);

  const grid = useMemo(() => buildMonthGrid(view.year, view.month), [view]);
  const todayMidnight = startOfToday();
  const selectedSlots = selectedDate ? slotsByDay.get(selectedDate) ?? [] : [];
  const monthEmpty = !isLoading && slotsByDay.size === 0;
  const canGoNext = (() => {
    const n = addMonths(view.year, view.month, 1);
    return isWithinRange(n.year, n.month);
  })();
  const canGoPrev = (() => {
    const p = addMonths(view.year, view.month, -1);
    return isWithinRange(p.year, p.month);
  })();

  // Every month within the bookable window, for the jump-to-month dropdown.
  const monthOptions = useMemo(() => {
    const opts: { year: number; month: number; label: string }[] = [];
    let y = today.getFullYear();
    let m = today.getMonth() + 1;
    for (let i = 0; i <= 24; i++) {
      opts.push({ year: y, month: m, label: formatMonthYear(y, m) });
      const n = addMonths(y, m, 1);
      y = n.year;
      m = n.month;
    }
    return opts;
  }, [today]);

  function jumpTo(y: number, m: number) {
    setSelectedDate(null);
    setView({ year: y, month: m });
    setPickerOpen(false);
  }

  function navigate(delta: number) {
    const next = addMonths(view.year, view.month, delta);
    if (!isWithinRange(next.year, next.month)) return;
    setSelectedDate(null); // clear stale day selection when the month changes
    setView(next);
  }

  function goToday() {
    setSelectedDate(null);
    setView({ year: today.getFullYear(), month: today.getMonth() + 1 });
  }

  // Mobile: a selected day opens a dedicated slots view (back returns to the
  // calendar), instead of the cramped side panel used on desktop.
  if (isMobile && selectedDate) {
    return (
      <div className="datetime-picker">
        <header>
          <button className="back-btn" onClick={() => setSelectedDate(null)}>‹ Volver</button>
          <span style={{ width: 60 }} />
        </header>
        <h2 style={{ textTransform: 'capitalize' }}>{formatShortDate(selectedDate)}</h2>
        <div className="dt-slot-list">
          {selectedSlots.map((time) => (
            <button key={time} className="dt-slot" onClick={() => onPick(selectedDate, time)}>
              {formatHora(time)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const emptyNotice = (
    <div className="dt-empty">
      <p>No hay citas disponibles en {formatMonthYear(view.year, view.month)}.</p>
      {canGoNext && (
        <button className="dt-next-month" onClick={() => navigate(1)}>
          Ver mes siguiente ›
        </button>
      )}
    </div>
  );

  return (
    <div className="datetime-picker">
      <header>
        <button className="back-btn" onClick={onBack}>‹ Volver</button>
        <button className="back-btn" onClick={goToday}>Hoy</button>
      </header>

      <div className="dt-monthnav">
        <button
          className="dt-arrow"
          onClick={() => navigate(-1)}
          disabled={!canGoPrev}
          aria-label="Mes anterior"
        >
          ‹
        </button>

        <div className="dt-monthpick">
          <button
            className="dt-month-btn"
            onClick={() => setPickerOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
          >
            {formatMonthYear(view.year, view.month)}
            <span className="dt-caret" aria-hidden="true">▾</span>
          </button>
          {pickerOpen && (
            <>
              <div className="dt-pick-backdrop" onClick={() => setPickerOpen(false)} />
              <ul className="dt-pick-list" role="listbox" ref={pickListRef}>
                {monthOptions.map((o) => {
                  const active = o.year === view.year && o.month === view.month;
                  return (
                    <li key={`${o.year}-${o.month}`}>
                      <button
                        ref={active ? activeOptionRef : undefined}
                        role="option"
                        aria-selected={active}
                        className={active ? 'active' : ''}
                        onClick={() => jumpTo(o.year, o.month)}
                      >
                        {o.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <button
          className="dt-arrow"
          onClick={() => navigate(1)}
          disabled={!canGoNext}
          aria-label="Mes siguiente"
        >
          ›
        </button>
      </div>

      <div className="dt-body">
        <div className="dt-calendar">
          <div className="grid">
            {['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'].map((d) => (
              <div key={d} className="dow">{d}</div>
            ))}
            {grid.map((cell, i) => {
              if (!cell) return <div key={i} />;
              const dateStr = toDateString(cell);
              const isPast = cell < todayMidnight;
              const hasSlots = slotsByDay.has(dateStr);
              const isSelected = dateStr === selectedDate;
              return (
                <button
                  key={i}
                  disabled={isPast || !hasSlots}
                  onClick={() => setSelectedDate(dateStr)}
                  className={`day ${hasSlots ? 'available' : ''} ${isSelected ? 'selected' : ''}`}
                  aria-label={dateStr}
                  aria-pressed={isSelected}
                >
                  {cell.getDate()}
                  {hasSlots && <span className="dot" />}
                </button>
              );
            })}
          </div>
          {isLoading && <Spinner />}
          {/* Mobile has no side panel, so surface the empty-month notice here. */}
          {isMobile && monthEmpty && emptyNotice}
        </div>

        {!isMobile && (
          <aside className="dt-slots" aria-live="polite">
            {monthEmpty ? (
              emptyNotice
            ) : !selectedDate ? (
              <p className="dt-hint">Seleccioná un día disponible para ver los horarios.</p>
            ) : (
              <>
                <h3>{formatShortDate(selectedDate)}</h3>
                <div className="dt-slot-list">
                  {selectedSlots.map((time) => (
                    <button key={time} className="dt-slot" onClick={() => onPick(selectedDate, time)}>
                      {formatHora(time)}
                    </button>
                  ))}
                </div>
              </>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
